<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/core/components/ui/tabs";
import {NotificationTemplate as INotificationTemplate} from "@/notification-template/interfaces";
import {NotificationTemplateIndexParams} from "@/core/http/resources/NotificationTemplateResource";
import {notificationTemplateApi} from "@/container";
import {Sparkles} from "@lucide/vue";
import RCSNotificationTemplateTab from "@/notification-template/components/sms-tabs/RCSNotificationTemplateTab.vue";
import SMSNotificationTemplateTab from "@/notification-template/components/sms-tabs/SMSNotificationTemplateTab.vue";
import NewFeatureTag from "@/core/components/tags/NewFeatureTag.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import NotificationTemplateService from "@/notification-template/services/NotificationTemplateService";
import LegalDisclaimer from "@/communication/components/LegalDisclaimer.vue";

const props = defineProps<{
    programId?: number | null;
    customerId: number;
    feature?: string | null;
    surveyId?: number;
    slugs?: string[];
}>();

defineEmits<{
    (e: 'templateSelected', notification: INotificationTemplate): void
    (e: 'customContent', content: { content: string, cost: number }): void
}>();

const activeTab = ref('sms');

const templates = ref<INotificationTemplate[]>([]);
const loading = ref(false);

onMounted(() => {
    const params: NotificationTemplateIndexParams = {
        customer_id: props.customerId,
        ...(props.surveyId ? {survey_id: props.surveyId} : {}),
        ...(props.programId ? {program_id: props.programId} : {}),
        ...(props.feature ? {feature: props.feature} : {}),
        ...(props.slugs ? {slugs: props.slugs} : {}),
        types: ['sms', 'rcs', 'rcs_flow']
    };

    loading.value = true;

    notificationTemplateApi()
        .index(params)
        .then((_templates) => {
            templates.value = _templates;
        })
        .finally(() => {
            loading.value = false;

            activeTab.value = rcsTemplates.value?.length ? 'rcs' : 'sms';
        });
});

const smsTemplates = computed(() => templates.value.filter(t => t.type === 'sms'));
const rcsTemplates = computed(() => templates.value.filter(t => NotificationTemplateService.isRcsType(t)));
</script>

<template>
  <BaseFlexSpinner v-if="loading" />
  <Tabs v-else v-model="activeTab" class="w-full">
    <TabsList class="w-full grid grid-cols-2">
      <TabsTrigger value="sms" class="w-full h-full">
        SMS
      </TabsTrigger>
      <TabsTrigger
        value="rcs"
        class="w-full"
      >
        <div class="flex items-center gap-2">
          <span>SMS enrichi</span>
          <NewFeatureTag class="pb-0.5" />
        </div>
        <span class="text-muted-foreground text-xs">3x plus d'impact</span>
      </TabsTrigger>
    </TabsList>
    <TabsContent value="sms">
      <SMSNotificationTemplateTab
        :templates="smsTemplates"
        :loading="loading"
        @custom-content="content => $emit('customContent', content)"
        @template-selected="template => $emit('templateSelected', template)"
      />
    </TabsContent>
    <TabsContent value="rcs">
      <div
        class="mt-1 mb-3 flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/40 px-3 py-2 text-sm text-primary"
      >
        <Sparkles class="w-4 h-4 shrink-0" />
        <p>
          Proposez une nouvelle expérience interactive à vos patients grâce à la technologie RCS : visuels, vidéos,
          liens, avec un taux d’ouverture 3x supérieur au SMS, et 10x supérieur au mail
        </p>
      </div>
      <LegalDisclaimer />
      <RCSNotificationTemplateTab
        :templates="rcsTemplates"
        :loading="loading"
        @selected="template => $emit('templateSelected', template)"
      />
    </TabsContent>
  </Tabs>
</template>

<style scoped>

</style>
